import { AxiosInstance } from 'axios';
import { moneyInterface } from '@/compiler/types';
import baseAxios from '@/helpers/forms/axios';

export interface LocalStripeAccountInvocingSettings {
    automaticTax: {
        enabled: boolean | null
    }
}

export interface LocalStripeAccount {
    id: string
    createdAt: string
    updatedAt: string
    accountId: string
    invoicingSettings: LocalStripeAccountInvocingSettings
};

export interface LocalStripeAccountUpdateForm {
    invoicingSettings: {
        automaticTax: {
            enabled: boolean | ''
        }
    }
}

export interface LocalCustomerPaymentMethod {
    id: string
    createdAt: string
    updatedAt: string
    paymentMethodId: string
    paymentMethodType: string
    publicLastFour: string
    localStripeCustomerId: string
};

export interface LocalStripeCustomer {
    id: string
    createdAt: string
    updatedAt: string
    customerId: string
    localStripeAccount: LocalStripeAccount
    defaultPaymentMethod: LocalCustomerPaymentMethod
    paymentMethods: LocalCustomerPaymentMethod[]
};

export default (axios: AxiosInstance = baseAxios): { fetchStripeServiceProviderAccount: () => Promise<LocalStripeAccount | null>; updateStripeServiceProviderAccount: (form: LocalStripeAccountUpdateForm) => Promise<LocalStripeAccount>; createStripeMerchantCustomer: () => Promise<LocalStripeCustomer>; fetchStripeMerchantCustomer: () => Promise<LocalStripeCustomer | null>; deletePaymentMethodFromStripeMerchantCustomer: (stripeMerchantCustomerId: string, paymentMethodId: string) => Promise<void>; setPaymentMethodAsDefaultForStripeMerchantCustomer: (stripeMerchantCustomerId: string, paymentMethodId: string) => Promise<void>; getStripeMerchantCustomerBalance: (stripeMerchantCustomerId: string) => Promise<moneyInterface>; deleteStripeMerchantCustomer: (stripeMerchantCustomerId: string) => Promise<void>; addStripePaymentMethodForMerchant: (stripeMerchantCustomerId: string, stripePaymentMethodId: string) => Promise<void>; fetchStripeTokenForStripeMerchantCustomer: (stripeMerchantCustomerId: string) => Promise<string>; } => ({
    fetchStripeServiceProviderAccount: async (): Promise<LocalStripeAccount | null> => {
        const response = await axios.get<{ stripeServiceProviderAccount: LocalStripeAccount | null }>('/api/payment-providers/stripe/accounts/service-provider');

        return response.data.stripeServiceProviderAccount;
    },

    updateStripeServiceProviderAccount: (form: LocalStripeAccountUpdateForm): Promise<LocalStripeAccount> => axios
        .put<{ stripeServiceProviderAccount: LocalStripeAccount }>('/api/payment-providers/stripe/accounts/service-provider', form)
        .then(response => response.data.stripeServiceProviderAccount),

    createStripeMerchantCustomer: async (): Promise<LocalStripeCustomer> => {
        const response = await axios.post<{ stripeMerchantCustomer: LocalStripeCustomer }>('/api/payment-providers/stripe/customers/merchant/create');

        return response.data.stripeMerchantCustomer;
    },

    fetchStripeMerchantCustomer: async (): Promise<LocalStripeCustomer | null> => {
        const response = await axios.get<{ stripeMerchantCustomer: LocalStripeCustomer | null }>('/api/payment-providers/stripe/customers/merchant');

        return response.data.stripeMerchantCustomer;
    },

    deletePaymentMethodFromStripeMerchantCustomer: async (stripeMerchantCustomerId: string, paymentMethodId: string): Promise<void> => {
        await axios.post(`/api/payment-providers/stripe/customers/merchant/${stripeMerchantCustomerId}/payment-methods/delete`, { paymentMethodId });
    },

    setPaymentMethodAsDefaultForStripeMerchantCustomer: async (stripeMerchantCustomerId: string, paymentMethodId: string): Promise<void> => {
        await axios.post(`/api/payment-providers/stripe/customers/merchant/${stripeMerchantCustomerId}/payment-methods/default`, { paymentMethodId });
    },

    getStripeMerchantCustomerBalance: async (stripeMerchantCustomerId: string): Promise<moneyInterface> => {
        const response = await axios.get<{ balance: moneyInterface }>(`/api/payment-providers/stripe/customers/merchant/${stripeMerchantCustomerId}/balance`);

        return {
            amount: response.data.balance.amount,
            currency: response.data.balance.currency,
        };
    },

    deleteStripeMerchantCustomer: async (stripeMerchantCustomerId: string): Promise<void> => {
        await axios.post(`/api/payment-providers/stripe/customers/merchant/${stripeMerchantCustomerId}/delete`);
    },

    addStripePaymentMethodForMerchant: async (stripeMerchantCustomerId: string, stripePaymentMethodId: string): Promise<void> => {
        await axios.post(`/api/payment-providers/stripe/customers/merchant/${stripeMerchantCustomerId}/payment-methods`, { stripePaymentMethodId });
    },

    fetchStripeTokenForStripeMerchantCustomer: async (stripeMerchantCustomerId: string): Promise<string> => {
        const response = await axios.get<{ clientSecret: string }>(`/api/payment-providers/stripe/customers/merchant/${stripeMerchantCustomerId}/setup-intent`);

        return response.data.clientSecret;
    },
});
